import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  RoleplayCard,
  RoleplayDebrief,
  RoleplaySessionResponse,
} from '@wundo/shared/roleplay.ts';
import { PracticeDebriefOverlay } from './PracticeDebriefOverlay.tsx';
import { PracticeSessionBriefStage } from './PracticeSessionBriefStage.tsx';
import { PracticeSessionEnd } from './PracticeSessionEnd.tsx';
import {
  PracticeSessionHeader,
  type PracticeSessionHeaderState,
} from './PracticeSessionHeader.tsx';
import { PracticeSessionLive, type LiveConversation } from './PracticeSessionLive.tsx';
import { PracticeWrapUpDialog } from './PracticeWrapUpDialog.tsx';
import { useElapsedSeconds } from './useElapsedSeconds.ts';
import { usePracticeEnd } from './usePracticeEnd.ts';
import type { VoiceAgentState } from '@/lib/roleplayCall.ts';
import { fetchRoleplays } from '@/lib/roleplays.ts';
import { useAudioSetup } from '@/lib/useAudioSetup.ts';

/** How long a practice conversation runs before it is asked whether it is done. */
const WRAP_UP_NUDGE_MS = 10 * 60 * 1_000;

/**
 * How much room the conversation is asking its host for. `brief` and `ended`
 * are read-and-click screens that a host can size to their content; `live` is
 * a conversation that wants all the height it can get.
 */
export type PracticeSessionPhase = 'brief' | 'live' | 'ended';

export interface PracticeSessionExperienceProps {
  session: RoleplaySessionResponse;
  /**
   * What stands in front of the persona on the header row, with the hairline
   * that separates the two drawn here. The dedicated address puts the logo
   * there because it is outside the app and nothing else on the screen says
   * whose it is; the modal on Oefenen has the app around it.
   */
  brand?: ReactNode;
  /** Back to wherever the host came from. Must be stable across renders. */
  onLeave: () => void;
  /**
   * For a modal that shows the report itself: hands it the feedback an end
   * answers with, null when nobody spoke. A session that is over renders
   * nothing here; the host reads that feedback from the session.
   */
  onDebrief?: (debrief: RoleplayDebrief | null) => void;
  /**
   * Told when this screen moves the conversation on, so a host that sizes
   * itself to it can follow. It opens on `practiceOpeningPhase`.
   */
  onPhaseChange?: (phase: PracticeSessionPhase) => void;
}

/** Before the conversation, the audio check, or the conversation itself. */
type PracticeStep =
  | { name: 'choosing' }
  | { name: 'checkingAudio' }
  | { name: 'live'; live: LiveConversation };

/** The ask to wrap up: not yet due, on screen, or answered. */
type WrapUpNudge = 'waiting' | 'open' | 'closed';

const HEADER_STATE = {
  brief: 'before',
  live: 'running',
  ended: 'over',
} as const satisfies Record<PracticeSessionPhase, PracticeSessionHeaderState>;

type PracticeStage =
  | { phase: 'ended'; debrief: RoleplayDebrief | null }
  | { phase: 'live'; live: LiveConversation }
  | { phase: 'brief' };

/**
 * One practice conversation, from the moment it is opened to the moment the
 * feedback exists. Everything on it is read from the session, so a reload
 * mid-conversation lands back in the same conversation. Leaving tells its host,
 * which decides what that means: a modal closes, the dedicated address navigates.
 */
export function PracticeSessionExperience({
  session,
  brand,
  onLeave,
  onDebrief,
  onPhaseChange,
}: PracticeSessionExperienceProps) {
  const queryClient = useQueryClient();
  const { persona } = session.scenario;

  /*
   * Someone who came in on a link, rather than signed in. Everything the rest
   * of the app offers is closed to them, so nothing here points at it.
   */
  const linkOnly = session.scoped_access;

  const [step, setStep] = useState<PracticeStep>(() => resumedStep(session));
  const [agentState, setAgentState] = useState<VoiceAgentState | null>(null);
  const [wrapUp, setWrapUp] = useState(false);
  const hangUp = useRef<(() => Promise<void>) | null>(null);

  /*
   * The microphone is only asked for on the audio check, and released the
   * moment the call takes it over — two things holding the same device is how
   * a conversation starts silent.
   */
  const audio = useAudioSetup(step.name === 'checkingAudio');

  /*
   * The feedback when the conversation ended on this screen, or null when
   * nobody spoke. Read right here, on the screen it was held on: half of the
   * people here have no Oefenen, and the rest came in on a link.
   */
  const [endedHere, setEndedHere] = useState<RoleplayDebrief | null | undefined>(
    undefined,
  );
  const ended =
    onDebrief === undefined ? debriefEndedHere(endedHere, session) : undefined;

  const stage = practiceStage(ended, step);

  function changeStep(next: PracticeStep) {
    setStep(next);
    onPhaseChange?.(practiceStage(ended, next).phase);
  }

  function endHere(debrief: RoleplayDebrief | null) {
    setEndedHere(debrief);
    onPhaseChange?.('ended');
  }

  /*
   * What was practised has changed, so the attempt counts and the feedback are
   * asked for again rather than read from the cache this screen was opened from.
   */
  const roleplayId = session.roleplay_id;
  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['roleplays'] });
    if (roleplayId !== null) {
      void queryClient.invalidateQueries({
        queryKey: ['roleplayAttempts', roleplayId],
      });
    }
  }, [queryClient, roleplayId]);

  /* The ask to wrap up stays open after a failed end, and closes once it goes through. */
  const [nudge, setNudge] = useState<WrapUpNudge>('waiting');
  const openNudge = useCallback(() => setNudge('open'), []);
  function closeNudge() {
    setNudge((current) => (current === 'open' ? 'closed' : current));
  }

  const { end, ending, endFailed, debriefFailed } = usePracticeEnd({
    session,
    hangUp,
    refresh,
    onDebrief,
    onEndedHere: endHere,
    onAnswered: closeNudge,
  });

  /*
   * The clock starts when the other side is actually there — in a spoken
   * conversation when the call connects — and stops the instant Beëindig is
   * pressed: the wait for the feedback is not part of the conversation.
   */
  const live = step.name === 'live' ? step.live : null;
  const running =
    ended === undefined &&
    !ending &&
    !debriefFailed &&
    live !== null &&
    (live.mode === 'chat' || agentState !== null);
  const elapsed = useElapsedSeconds(running);
  useWrapUpTimer(running && nudge === 'waiting' && !wrapUp, openNudge);
  const card = usePractisedCard(session);

  /** Back to whatever this conversation is held over, counting it again on the way. */
  function leave() {
    refresh();
    onLeave();
  }

  /*
   * The way out before there is anything to end. Nothing is ended, so the link
   * still opens the session. Somebody signed in goes back to Oefenen; somebody
   * on a link has nowhere to go, so the screen closes itself.
   */
  function abandon() {
    if (linkOnly) {
      endHere(null);
      return;
    }

    leave();
  }

  /*
   * A session that is over, whether it already was when this screen opened or
   * its feedback was written while the screen waited, has a new attempt to count.
   */
  const { status } = session;
  useEffect(() => {
    if (status === 'completed') {
      refresh();
    }
  }, [status, refresh]);

  /* Over, and the feedback is the host's to show: nothing here belongs on it. */
  if (onDebrief !== undefined && (status === 'completed' || status === 'aborted')) {
    return null;
  }

  return (
    <>
      <PracticeSessionHeader
        brand={brand}
        title={`${persona.name}, ${persona.role}`}
        typeLabel={card?.type_label}
        state={HEADER_STATE[stage.phase]}
        linkOnly={linkOnly}
        elapsed={elapsed}
        ending={ending}
        onAbandon={abandon}
        onEnd={end}
      />

      {stage.phase === 'ended' && (
        <div className='min-h-0 flex-1 overflow-y-auto px-5 pt-6.5 pb-16'>
          <PracticeSessionEnd debrief={stage.debrief} />
        </div>
      )}

      {stage.phase === 'brief' && (
        <PracticeSessionBriefStage
          session={session}
          checkingAudio={step.name === 'checkingAudio'}
          audio={audio}
          onCheckAudio={() => changeStep({ name: 'checkingAudio' })}
          onLeaveAudioCheck={() => changeStep({ name: 'choosing' })}
          onBegun={(begun) => changeStep({ name: 'live', live: begun })}
        />
      )}

      {stage.phase === 'live' && (
        <PracticeSessionLive
          live={stage.live}
          session={session}
          call={{
            hangUpRef: hangUp,
            agentState,
            onAgentState: setAgentState,
            microphoneId: audio.microphoneId,
            speakerId: audio.speakerId,
          }}
          wrapUpSuggested={wrapUp}
          onSuggestEnd={() => setWrapUp(true)}
          ending={ending}
          endFailed={endFailed}
          onEnd={end}
        />
      )}

      {nudge === 'open' && (
        <PracticeWrapUpDialog ending={ending} onEnd={end} onKeepGoing={closeNudge} />
      )}

      {(ending || debriefFailed) && (
        <PracticeDebriefOverlay
          failed={debriefFailed}
          linkOnly={linkOnly}
          onRetry={end}
          onLeave={leave}
        />
      )}
    </>
  );
}

/** The phase a host that reads the feedback itself sees the screen open on. */
export function practiceOpeningPhase(
  session: RoleplaySessionResponse,
): PracticeSessionPhase {
  return resumedStep(session).name === 'live' ? 'live' : 'brief';
}

/* A conversation that was already running when the screen opened picks up again. */
function resumedStep(session: RoleplaySessionResponse): PracticeStep {
  if (session.status !== 'in_progress' || session.mode === null) {
    return { name: 'choosing' };
  }

  return { name: 'live', live: { mode: session.mode, voice: session.voice ?? null } };
}

function debriefEndedHere(
  endedHere: RoleplayDebrief | null | undefined,
  session: RoleplaySessionResponse,
): RoleplayDebrief | null | undefined {
  if (endedHere !== undefined) return endedHere;
  if (session.status === 'aborted') return null;
  if (session.status === 'completed') return session.debrief;
  return undefined;
}

function practiceStage(
  ended: RoleplayDebrief | null | undefined,
  step: PracticeStep,
): PracticeStage {
  if (ended !== undefined) return { phase: 'ended', debrief: ended };
  if (step.name === 'live') return { phase: 'live', live: step.live };
  return { phase: 'brief' };
}

/*
 * Ten minutes in, once. A practice conversation has no natural end, and the
 * feedback is written from the whole of it. Dropped entirely if the character
 * has already asked in its own words: two ways of being asked is nagging.
 */
function useWrapUpTimer(due: boolean, onDue: () => void) {
  useEffect(() => {
    if (!due) {
      return;
    }

    const timer = setTimeout(onDue, WRAP_UP_NUDGE_MS);
    return () => clearTimeout(timer);
  }, [due, onDue]);
}

/*
 * The library card this attempt belongs to, for the type on the header. Not
 * asked for on a link visit: the library is not theirs to read, and the type
 * is a nicety the header does without.
 */
function usePractisedCard(session: RoleplaySessionResponse): RoleplayCard | undefined {
  const { data: roleplays } = useQuery({
    queryKey: ['roleplays'],
    queryFn: fetchRoleplays,
    enabled: !session.scoped_access,
  });

  if (session.roleplay_id === null) return undefined;

  return [...(roleplays?.library ?? []), ...(roleplays?.personal ?? [])].find(
    (candidate) => candidate.id === session.roleplay_id,
  );
}
